import { createContext, useContext, PropsWithChildren, useState, useEffect, useCallback } from 'react';
import { CheckboxInputProps } from '@/components/functional/CheckboxInput/CheckboxInput.tsx';

type RadioContextProps = {
  value: any;
  setValue: (value: any) => void;
  error?: string;
} & Pick<CheckboxInputProps, 'color'>;

const RadioContext = createContext<RadioContextProps>(undefined!);

export type RadioProviderProps<T> = {
  value?: T;
  onChange?: (value: T) => void;
  error?: string;
} & Pick<CheckboxInputProps, 'color'>;

export function RadioProvider<T>({
  children,
  value,
  onChange,
  error,
  color,
}: PropsWithChildren<RadioProviderProps<T>>) {
  const [localValue, setLocalValue] = useState(value);

  const setLocalValueWrapper = useCallback(
    (newValue: T) => {
      setLocalValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  useEffect(() => {
    setLocalValueWrapper(value as T);
  }, [value, setLocalValueWrapper]);

  return (
    <RadioContext.Provider
      value={{
        value: localValue,
        setValue: setLocalValueWrapper,
        error,
        color,
      }}
    >
      {children}
    </RadioContext.Provider>
  );
}

export function useRadioContext(): RadioContextProps {
  const context = useContext(RadioContext);

  if (typeof context === 'undefined') {
    throw new Error('useRadioContext should be used within the RadioContext provider!');
  }

  return context;
}
